/**
 * @file VeriomeSelectField.js
 * @description
 *
 * note:
 * options must be in the format:
 * 
 * [
        {
            "value": "autosomalDominant",
            "label": "Autosomal Dominant",
            "disabled": false,  // optional
        },
 *  ] 
 * 
 *  the selected value is returned via the provided handleChange() function 
 *        
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Dec 29, 2021 22:23
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

import { arrayExists, objectExists, objectKeyExists, stringExists } from 'src/@common/utilities';

function VeriomeSelectField(props) {
    const {
        fieldName,
        fieldLabel,
        className,
        currentValue,  // string
        options,
        fieldErrors = {},
        helpText = '',
        isDisabled = false,
        isFocus = false,
        isFullWidth = false,
        isRequired = false,
        sxStyle = { minWidth: '150px', mr: '5px', mb: '5px' },
        color = 'secondary',
        variant = 'filled',
        handleBlur = () => { },
        handleChange,
    } = props;

    const [helperText, setHelperText] = useState('');
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(null);

    const loading = false;

    useEffect(() => {
        // find the current value in the options
        if (stringExists(currentValue) && arrayExists(options)) {

            const existingOption = options.find((item) => {
                return item.value === currentValue;
            });

            if (existingOption) {
                setValue(existingOption);
            } else {
                setValue({
                    value: '',
                    label: '',
                });
            }

        } else {
            setValue({
                value: '',
                label: '',
            });
        }

    }, [currentValue, options]);

    useEffect(() => {

        if (fieldErrors[fieldName]) {
            setHelperText(fieldErrors[fieldName])
        } else {
            if (objectExists(value)) {
                if (stringExists(helpText)) {
                    setHelperText(helpText);
                } else {
                    setHelperText(value.id)
                }
            } else {
                if (stringExists(helpText)) {
                    setHelperText(helpText);
                } else {
                    setHelperText('')
                }
            }
        }

    }, [fieldErrors, fieldName, helpText, value]);

    const onChange = (event, newValue) => {
        // this change event provides the newly selected object.
        // update both the local and parent state.

        let selectedValue = '';

        // // set the local state
        setValue(newValue);

        if (objectExists(newValue)) {
            selectedValue = newValue.value;
        }

        // wrap in a synthetic event
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: selectedValue,
            }
        };

        // call the parent change handler to return the selected value
        handleChange(newEvent, fieldName);
    };

    return (
        <div className={className} >
            <Autocomplete
                id={fieldName}
                name={fieldName}
                disablePortal={true}
                disableClearable
                autoSelect
                open={open}
                onOpen={() => {
                    setOpen(true);
                }}
                onClose={() => {
                    setOpen(false);
                }}
                getOptionLabel={(option) => {
                    return option.label
                }}
                isOptionEqualToValue={(option, value) => {
                    if (objectExists(value) &&
                        stringExists(value.value)) {
                        return option.value === value.value
                    } else {
                        return true;
                    }
                }}
                getOptionDisabled={(option) => {
                    if (objectKeyExists(option, 'disabled') &&
                        option.disabled === true) {
                        return true;
                    } else {
                        return false;
                    }
                }}
                includeInputInList
                disabled={isDisabled}
                onBlur={() => {
                    handleBlur(fieldName);
                }}
                onChange={onChange}
                options={options}
                loading={loading}
                noOptionsText={'No options found'}
                value={value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={fieldLabel}
                        size="small"
                        margin="dense"
                        variant={variant}
                        error={Boolean(fieldErrors[fieldName])}
                        autoFocus={isFocus}
                        color={color}
                        fullWidth={isFullWidth}
                        helperText={helperText}
                        required={isRequired}
                        sx={sxStyle}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </div>
    );
}

VeriomeSelectField.propTypes = {
    fieldLabel: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    className: PropTypes.string,
    currentValue: PropTypes.string,
    options: PropTypes.array,
    fieldErrors: PropTypes.object,
    helpText: PropTypes.string,
    isDisabled: PropTypes.bool,
    isFocus: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    isRequired: PropTypes.bool,
    sxStyle: PropTypes.object,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
};

export default VeriomeSelectField;
